import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import { useState, useRef, useEffect } from "react";
import QRCodeStyling from "qr-code-styling";
import ANALYTICS from "../Assets/analytics.png";
import { useNavigate } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { UserAuth } from "../../context/AuthContext";

function QRCodeModal(props: any) {
  const qrRef = useRef<HTMLDivElement>(null);
  const [qrCode] = useState<QRCodeStyling>(new QRCodeStyling(props.options));
  const [page, setPage] = useState<number>(1);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const { user, roles } = UserAuth();
  const { premium, business } = roles;

  let shortyUrl: any;
  let shortUrlId: any;

  useEffect(() => {
    if (user) {
      if (premium) {
        setPage(2);
      }
    }
  }, [user, premium]);

  useEffect(() => {
    qrCode.append(qrRef.current!);
    qrCode.update(props.options);
  });

  const onDownloadClick = async (ext: any) => {
    setPage(3);
    const shortenedUrl = await shortenUrl(props.qrdata.fullUrl);
    qrToImg(ext);
  };

  const qrToImg = (ext: any) => {
    let imageData: any;
    qrCode
      .getRawData(ext)
      .then((rawData) => {
        if (rawData) {
          let mimeType = `image/${ext}`;
          if (ext === "svg") {
            mimeType = "image/svg+xml";
          }

          // Create a Blob directly from the raw data
          const blob = new Blob([rawData], { type: mimeType });
          const file = new File([blob], `qrcode.${ext}`, {
            type: mimeType,
          });
          return uploadImage(file);
        } else {
          throw new Error("Data is null");
        }
      })
      .then((data) => {
        imageData = data;
        return data;
      })
      .then(() => {
        // Calculate expiry date if days are selected
        let expiryDate = null;
        if (props.qrdata.expiry) {
          const days = parseInt(props.qrdata.expiry);
          expiryDate = new Date();
          expiryDate.setDate(expiryDate.getDate() + days);
        }

        const updatedQrData = {
          title: props.qrdata.title,
          fullUrl: props.qrdata.fullUrl,
          userId: user?.uid,
          image: imageData,
          shortenedUrl: shortyUrl,
          urlId: shortUrlId,
          description: props.qrdata.description || "",
          premium: true,
          totalVisits: 0,
          password: props.qrdata.password || "",
          expiry: expiryDate,
          group: props.qrdata.group || "",
        };
        handleSave(updatedQrData);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const uploadImage = async (file: File) => {
    setLoading(true);
    const token = await user.getIdToken();
    const formData = new FormData();
    formData.append("image", file, file.name);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/aws/s3/upload`,
        {
          method: "POST",
          body: formData,
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        setLoading(false);
        const errorData = await response.json();
        throw new Error(
          `HTTP Error: ${response.status} - ${JSON.stringify(errorData)}`
        );
      }

      const data = await response.json();
      return data.imageUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      setLoading(false);
      throw error;
    }
  };

  const handleSave = async (data: any) => {
    setLoading(true);
    const token = await user.getIdToken();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/qrcodes/`,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        setLoading(false);
        const errorData = await response.json();
        console.error("Server response:", errorData);
        throw new Error(
          `HTTP Error: ${response.status} - ${JSON.stringify(errorData)}`
        );
      }

      const responseData = await response.json();
      setLoading(false);
      navigate("/dashboard/qrcodes");
    } catch (error) {
      console.error("Error saving QR code:", error);
      setLoading(false);
    }
  };

  const shortenUrl = async (url: string) => {
    setLoading(true);
    const token = await user.getIdToken();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/short/`, {
        method: "POST",
        body: JSON.stringify({
          longUrl: url,
          title: props.qrdata.title,
          userId: user?.uid,
        }),
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setLoading(false);
        throw new Error(
          `HTTP Error: ${response.status} - ${response.statusText}`
        );
      }

      const responseData = await response.json();
      props.options.data = responseData.shortUrl;
      shortyUrl = responseData.shortUrl;
      shortUrlId = responseData.urlId;
      qrCode.append(qrRef.current!);
      qrCode.update(props.options);
      setLoading(false);
      return responseData.shortUrl;
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  return (
    <div className="QRCodeModal">
      {loading && <LoadingSpinner />}
      {page === 1 && (
        <Modal
          {...props}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Premium Plan Upgrade
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p>
              You are currently using our Free Plan which provides Basic
              features. We recommend upgrading to our Premium Plan to unlock
              many benefits and features. Generate unlmited QR Codes with
              tracking and detailed analytics.
            </p>

            <div className="modal__container">
              <div className="modal__container__features">
                <b className="modal__bold">Premium Plan Features:</b>
                <ul className="modal__container__list">
                  <li>Unlimited QR Codes</li>
                  <li>Visit Tracking with Analytics</li>
                  <li>Premium Frames</li>
                  <li>Premium Color Gradients</li>
                  <li>Priority Customer Support</li>
                </ul>
              </div>

              <img
                src={ANALYTICS}
                className="modal__container__analytics"
                alt="Analytics Graph"
              ></img>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              className="modal__cta-btn"
              onClick={() => navigate("/dashboard/plans")}
            >
              Upgrade to Premium Plan ⚡
            </Button>
            <Button onClick={() => setPage(2)} className="modal__free-btn">
              Continue with Free Plan
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {page === 2 && (
        <Modal
          {...props}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          dialogClassName="qr-download-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Save your QR Code
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {!premium && (
              <p>
                You are currently using our Free Plan which provides Basic
                features. We recommend upgrading to our Premium Plan to unlock
                many benefits and features. Generate unlmited QR Codes with
                tracking and detailed analytics.
              </p>
            )}

            {premium && (
              <p>
                You are currently using our Premium Plan which provides
                analytics. Your QR Code will be added to your dashboard.
              </p>
            )}

            <div ref={qrRef} className="modal__qrCode"></div>
          </Modal.Body>

          <Modal.Footer>
            <div className="download-button-container">
              <Button
                id="save-qr-button"
                onClick={() => onDownloadClick("png")}
                className="custom-button"
              >
                Confirm QR Code
              </Button>
            </div>
            {!premium && (
              <Button onClick={() => setPage(1)} className="modal__free-btn">
                Cancel
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {page === 3 && (
        <Modal
          {...props}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header>
            <Modal.Title id="contained-modal-title-vcenter">
              Redirecting...
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p>
              Your QR Code has been generated and saved. Redirecting to your QR
              List where you can view it.
            </p>

            <Spinner animation="grow" role="status" className="modal__spinner">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Modal.Body>

          <Modal.Footer></Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default QRCodeModal;
